import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import RadioGroup from '@material-ui/core/RadioGroup';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import PreviewIcon from '@material-ui/icons/ImageSearch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/Create';
import LinkIcon from '@material-ui/icons/Link';
import { getMarketName, getLanguage } from '../../features/app/selectors';
import PpiTable from './PpiTable';
import { ppiInProgressRoute, ppiPublishedRoute } from '../../routes';
import { States } from '../../features/PpiContainer/constants';

const useStyles = makeStyles({
  radioGroupRoot: {
    display: 'block'
  },
  radioButtonRoot: {
    '& .MuiSvgIcon-root': {
      fontSize: '1.2rem'
    }
  },
  ppiLabelContainer: {
    userSelect: 'none'
  }
});

const PpiList = ({
  ppis = [],
  selectedPpiDataId,
  onChangeSelectedPpi,
  openPreview,
  language,
  isExcluded,
  localPpiCopy,
  removeIsExclude,
  globalVariant = false
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const userMarket = useSelector(getMarketName);
  const [selected, setSelected] = useState(isExcluded);
  const marketLanguage = useSelector(getLanguage);
  const languages = localPpiCopy === 'true' ? marketLanguage : language;

  if (!ppis) {
    return null;
  }

  function editPpi(ppiId) {
    history.push(ppiInProgressRoute(ppiId));
  }

  function openPublishedPpi(ppiId) {
    history.push(ppiPublishedRoute(ppiId));
  }

  function handleExcludeChange(event, data) {
    setSelected(!selected);
    onChangeSelectedPpi(event, { selected }, data);
  }
  const dataToPass = isExcluded === undefined ? false : isExcluded;
  const exclude = removeIsExclude ? (
    ''
  ) : (
    <FormControlLabel
      value={dataToPass}
      control={
        <Radio
          classes={{ root: classes.radioButtonRoot }}
          onClick={event => handleExcludeChange(event)}
          checked={dataToPass}
        />
      }
      label={
        <div className={classes.ppiLabelContainer}>
          <Typography variant="body2">Exclude</Typography>
        </div>
      }
    />
  );

  function buildEnrichmentRow({ id, market, data }) {
    const { presentationName } = data.info[languages];
    const status = data.enrichmentStatus;

    const name = (
      <FormControlLabel
        value={data.id}
        control={
          <Radio
            classes={{ root: classes.radioButtonRoot }}
            onClick={event => onChangeSelectedPpi(event, { id }, data)}
            checked={data.id === selectedPpiDataId}
          />
        }
        label={
          <div className={classes.ppiLabelContainer}>
            <Typography variant="body2">{presentationName}</Typography>
          </div>
        }
      />
    );

    const actions = (
      <>
        <Tooltip
          title={formatMessage({
            id: 'ppsi.previewInstructionTooltip'
          })}
        >
          <IconButton
            onClick={() => openPreview(data, languages)}
            color="secondary"
            size="medium"
          >
            <PreviewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {status === States.DRAFT && market === userMarket && (
          <Tooltip
            title={formatMessage({
              id: 'ppi.editPpiActionTooltip'
            })}
          >
            <IconButton
              onClick={() => editPpi(id)}
              color="secondary"
              size="medium"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {(status === States.PUBLISHED || status === States.UNPUBLISHED) &&
          market === userMarket &&
          localPpiCopy !== 'true' && (
            <Tooltip
              title={formatMessage({
                id: 'ppi.openPpiActionTooltip'
              })}
            >
              <IconButton
                onClick={() => openPublishedPpi(id)}
                color="secondary"
                size="medium"
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
      </>
    );

    return {
      id: data.id,
      name,
      status,
      actions
    };
  }

  let rows = [];
  rows = rows.concat(
    ppis
      .filter(ppi => ppi.publishedPPIEnrichmentData)
      .map(({ id, itemCode, market, publishedPPIEnrichmentData: data }) =>
        buildEnrichmentRow({ id, itemCode, market, data })
      )
  );
  if (localPpiCopy !== 'true' && !globalVariant) {
    rows = rows.concat(
      ppis
        .filter(ppi => ppi.inProgressPPIEnrichmentData)
        .map(({ id, itemCode, market, inProgressPPIEnrichmentData: data }) =>
          buildEnrichmentRow({ id, itemCode, market, data })
        )
    );
  }
  return (
    <RadioGroup name="ppis" classes={{ root: classes.radioGroupRoot }}>
      {rows.length === 0 ? (
        <p style={{ color: '#9c9a98', fontSize: '16px', marginBottom: '30px' }}>
          No ingredient found!
        </p>
      ) : (
        <PpiTable rows={rows} exclude={exclude} key={Math.random()} />
      )}
    </RadioGroup>
  );
};

PpiList.propTypes = {
  language: PropTypes.string.isRequired,
  onChangeSelectedPpi: PropTypes.func,
  openPreview: PropTypes.func,
  ppis: PropTypes.array,
  selectedPpiDataId: PropTypes.string
};

export default PpiList;
